// ScopedImpactMatrix — Organizational Exposure matrix.
//
// Layout (per product spec):
//   - Vertical rows = capability areas, in the order configured in the
//     client's profile (`clientProfile.matrixConfig.columns` / verbatim
//     `clientProfile.capabilityAreas`).
//   - Horizontal columns = business segments, in the configured order.
//   - The full client matrix is ALWAYS rendered. Rows/columns are never
//     hidden — missing combinations show as visible NO_IMPACT cells.
//   - The Selected/All toggle filters which signal impacts contribute to
//     a cell's aggregated impactLevel + impactCount, but never changes the
//     axes.

import React, { useMemo, useState } from "react";
import {
    buildOrganizationalExposureMatrix,
    CLIENT_POSITIONING_TONE,
    clientPositioningLabel,
    levelLabel,
    levelRank,
} from "../../../lib/clients";

const LEVEL_CELL = {
    VERY_HIGH: { bg: "bg-[hsl(var(--ink))]", text: "text-white" },
    HIGH: { bg: "bg-[hsl(var(--accent))]", text: "text-white" },
    MEDIUM: { bg: "bg-[hsl(var(--warning))]", text: "text-white" },
    LOW: { bg: "bg-[hsl(var(--surface-sunken))] border border-[hsl(var(--line))]", text: "text-[hsl(var(--ink-soft))]" },
};

const isImpacted = (impactLevel) => {
    const u = (impactLevel || "").toUpperCase();
    return u && u !== "NO_IMPACT";
};

const Badge = ({ tone = "muted", children, "data-testid": testId }) => (
    <span className={`badge ${tone === "accent" ? "badge-accent" : tone === "positive" ? "badge-positive" : tone === "warning" ? "badge-warning" : tone === "negative" ? "badge-negative" : tone === "regulatory" ? "badge-regulatory" : tone === "ink" ? "badge-ink" : ""}`} data-testid={testId}>
        {children}
    </span>
);

const ToggleGroup = ({ value, onChange, options, testId }) => (
    <div className="inline-flex border border-[hsl(var(--line))] rounded-sm overflow-hidden" role="tablist" data-testid={testId}>
        {options.map((o) => (
            <button
                key={o.value}
                type="button"
                role="tab"
                aria-selected={value === o.value}
                onClick={() => onChange(o.value)}
                className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-medium transition-colors ${value === o.value ? "bg-[hsl(var(--ink))] text-white" : "bg-[hsl(var(--surface))] text-[hsl(var(--ink-soft))] hover:bg-[hsl(var(--surface-sunken))]"}`}
                data-testid={`${testId}-opt-${o.value}`}
            >
                {o.label}
            </button>
        ))}
    </div>
);

// Resolve the cell-level Recommended Response per the v2.1 spec contract.
// Cell-level recommendation is anchored on `impactCellAssessments`. We never
// fall through to TrackCards or Action Backlog here.
const _firstNonEmpty = (...xs) => {
    for (const x of xs) {
        if (typeof x === "string" && x.trim()) return x.trim();
    }
    return null;
};
const readCellRecommendedAction = (impact) => _firstNonEmpty(
    impact?.recommendedAction,
    impact?.RecommendedAction,
    impact?.recommendedResponse,
    impact?.responseGuidance,
    impact?.actionGuidance,
    impact?.recommendation,
);

const ScopedImpactMatrix = ({ selectedTrack, exposureMatrix, impactCellAssessments, clientProfile, onSelectTrack }) => {
    const [scope, setScope] = useState("selected");
    const [openKey, setOpenKey] = useState(null);

    const matrix = useMemo(() => buildOrganizationalExposureMatrix({
        exposureMatrix,
        clientProfile,
        impactCellAssessments,
        scope,
        selectedTrackId: selectedTrack?.trackId || null,
    }), [exposureMatrix, clientProfile, impactCellAssessments, scope, selectedTrack]);

    // Short, display-friendly client name for the "[Client] foothold" section
    // heading. Prefer the matrixConfig label, fall back to the first impact
    // row's clientName, and finally to "Client".
    const clientDisplayName = useMemo(() => {
        const fromProfile = clientProfile?.matrixConfig?.clientDisplayName
            || clientProfile?.client?.displayName
            || clientProfile?.client?.shortName;
        if (typeof fromProfile === "string" && fromProfile.trim()) return fromProfile.trim();
        // Walk cells to find any impact row carrying clientName.
        for (const k of Object.keys(matrix.cells || {})) {
            const impacts = (matrix.cells[k] && matrix.cells[k].impacts) || [];
            for (const r of impacts) {
                const n = r?.clientName;
                if (typeof n === "string" && n.trim()) {
                    // Trim long legal names ("LBBW Landesbank Baden-Württemberg"
                    // → "LBBW") at the first dash, comma, or whitespace.
                    return n.split(/[\s\-–,]/)[0] || n;
                }
            }
        }
        return "Client";
    }, [clientProfile, matrix]);

    const scopeLabel = scope === "selected"
        ? (selectedTrack ? `for ${selectedTrack.stableTrackName || selectedTrack.trackId}` : "for selected signal")
        : "across all signals";

    const noAxes = matrix.businessSegments.length === 0 || matrix.capabilityAreas.length === 0;

    return (
        <section className="card-flat overflow-hidden" data-testid="impact-matrix">
            <header className="px-4 py-3 border-b border-[hsl(var(--line))] flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="eyebrow">Organizational exposure</div>
                    <h3 className="display text-base mt-0.5 leading-snug">Where this signal touches the organization</h3>
                    <p className="text-[10px] text-[hsl(var(--mute))] mt-0.5">Rows = capability areas · columns = business segments. Cells show market exposure intensity, not the organization&#39;s readiness, {scopeLabel}.</p>
                </div>
                <ToggleGroup
                    value={scope}
                    onChange={(v) => { setScope(v); setOpenKey(null); }}
                    options={[
                        { value: "selected", label: "Selected" },
                        { value: "all", label: "All" },
                    ]}
                    testId="impact-scope"
                />
            </header>

            {noAxes ? (
                <div className="p-4 text-xs text-[hsl(var(--mute))]" data-testid="impact-matrix-empty">
                    No matrix axes configured in this client&#39;s profile.
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="text-[11px] w-full">
                            <thead>
                                <tr>
                                    <th className="px-3 py-2 text-left text-[hsl(var(--mute))] uppercase tracking-wider text-[10px] sticky left-0 bg-[hsl(var(--surface))] min-w-[170px]">Capability area</th>
                                    {matrix.businessSegments.map((bs) => (
                                        <th key={bs} className="px-1.5 py-2 text-[9px] text-[hsl(var(--mute))] uppercase tracking-wider text-center font-medium" title={bs}>
                                            <div className="truncate max-w-[130px] mx-auto">{bs}</div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {matrix.capabilityAreas.map((ca) => (
                                    <tr key={ca} className="border-t border-[hsl(var(--line))]">
                                        <td className="px-3 py-1.5 sticky left-0 bg-[hsl(var(--surface))] max-w-[200px]">
                                            <div className="text-[11px] font-medium text-[hsl(var(--ink))] leading-snug" title={ca}>{ca}</div>
                                        </td>
                                        {matrix.businessSegments.map((bs) => {
                                            const key = `${ca}__${bs}`;
                                            const cell = matrix.cells[key];
                                            const impacted = cell && isImpacted(cell.impactLevel);
                                            if (!impacted) {
                                                return (
                                                    <td key={bs} className="px-1.5 py-1.5 text-center">
                                                        <div
                                                            className="mx-auto w-12 h-6 rounded-sm border border-dashed border-[hsl(var(--line))] bg-[hsl(var(--surface-sunken))]/40 flex items-center justify-center text-[9px] text-[hsl(var(--mute))]"
                                                            title={`${ca} × ${bs}: no impact ${scopeLabel}`}
                                                            data-testid={`impact-cell-empty-${ca}-${bs}`}
                                                        >
                                                            —
                                                        </div>
                                                    </td>
                                                );
                                            }
                                            const lv = (cell.impactLevel || "").toUpperCase();
                                            const style = LEVEL_CELL[lv] || LEVEL_CELL.LOW;
                                            const isOpen = openKey === key;
                                            const count = cell.impactCount || (cell.impacts ? cell.impacts.length : 0);
                                            return (
                                                <td key={bs} className="px-1.5 py-1.5 text-center">
                                                    <button
                                                        type="button"
                                                        data-testid={`impact-cell-${ca}-${bs}`}
                                                        onClick={() => setOpenKey(isOpen ? null : key)}
                                                        aria-pressed={isOpen}
                                                        title={`${ca} × ${bs}: ${cell.impactLevel}${count ? ` (${count})` : ""}`}
                                                        className={`mx-auto w-12 h-6 rounded-sm flex items-center justify-center text-[10px] font-medium num transition-transform ${style.bg} ${style.text} ${isOpen ? "ring-2 ring-[hsl(var(--accent))] ring-offset-1" : "hover:scale-[1.05]"}`}
                                                    >
                                                        <span>{levelLabel(cell.impactLevel).split(" ")[0]}</span>
                                                        {count > 1 && (
                                                            <span className="text-[8px] opacity-80 ml-0.5">×{count}</span>
                                                        )}
                                                    </button>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="px-4 py-2 border-t border-[hsl(var(--line))] flex items-center gap-3 text-[10px] text-[hsl(var(--mute))] flex-wrap">
                        <span>Legend:</span>
                        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-[hsl(var(--ink))]" />Very high</span>
                        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-[hsl(var(--accent))]" />High</span>
                        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-[hsl(var(--warning))]" />Medium</span>
                        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-[hsl(var(--surface-sunken))] border border-[hsl(var(--line))]" />Low</span>
                        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm border border-dashed border-[hsl(var(--line))]" />No impact</span>
                    </div>

                    {matrix.count === 0 && scope === "selected" && (
                        <div className="px-4 py-2 border-t border-[hsl(var(--line))] text-[11px] text-[hsl(var(--mute))]" data-testid="impact-matrix-no-signal-rows">
                            The selected signal does not impact any cell in this matrix. Switch to &ldquo;All&rdquo; to see the full week&apos;s exposure.
                        </div>
                    )}

                    {openKey && matrix.cells[openKey] && (
                        <div className="border-t border-[hsl(var(--line))] p-4 bg-[hsl(var(--surface-sunken))]" data-testid="impact-cell-detail">
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="eyebrow">{matrix.cells[openKey].capabilityArea} × {matrix.cells[openKey].businessSegment}</div>
                                {/* Cell-level badge shows the STRUCTURAL impact level (ImpactLevel),
                                    not the client-positioning overlay. The latter is per-impact below. */}
                                {matrix.cells[openKey].impactLevel && <Badge tone="ink">{levelLabel(matrix.cells[openKey].impactLevel)}</Badge>}
                            </div>
                            <ul className="mt-3 space-y-3">
                                {[...(matrix.cells[openKey].impacts || [])]
                                    .sort((a, b) => levelRank(b.impactLevel) - levelRank(a.impactLevel))
                                    .map((r, i) => {
                                        const positioning = r.clientPositioningCategory || r.ClientPositioningCategory || null;
                                        const positioningLbl = clientPositioningLabel(positioning);
                                        const positioningTone = CLIENT_POSITIONING_TONE[(positioning || "").toUpperCase()] || "muted";
                                        const structuralBody = r.structuralImpactBasis || r.impactMechanism || null;
                                        const positioningBody = r.clientSpecificRationale || r.clientPositioningBasis || null;
                                        const recommended = readCellRecommendedAction(r);
                                        return (
                                            <li key={`${r.trackId || r.stableTrackName || "row"}|${openKey}|${i}`} className="bg-[hsl(var(--surface))] border border-[hsl(var(--line))] rounded p-3" data-testid={`impact-row-${i}`}>
                                                <div className="flex items-center gap-2 flex-wrap text-[11px]">
                                                    {r.impactLevel && <Badge tone="ink">{levelLabel(r.impactLevel)}</Badge>}
                                                    {positioningLbl && (
                                                        <Badge tone={positioningTone} data-testid={`impact-row-${i}-positioning`}>{positioningLbl}</Badge>
                                                    )}
                                                    {r.stableTrackName && (
                                                        <button type="button" onClick={() => r.trackId && onSelectTrack && onSelectTrack(r.trackId)} className="text-[hsl(var(--accent-ink))] hover:underline font-medium">{r.stableTrackName}</button>
                                                    )}
                                                </div>

                                                {/* 1. Structural Impact — organizational exposure if the signal scales */}
                                                {structuralBody && (
                                                    <div className="mt-2" data-testid={`impact-row-${i}-structural`}>
                                                        <div className="text-[hsl(var(--mute))] uppercase tracking-wider text-[10px] font-medium">Structural impact</div>
                                                        <p className="text-xs text-[hsl(var(--ink-soft))] leading-relaxed mt-1">{structuralBody}</p>
                                                    </div>
                                                )}

                                                {/* 2. Client Foothold — client-specific overlay (rationale only;
                                                       the positioning enum is already shown as the pill above) */}
                                                {positioningBody && (
                                                    <div className="mt-2" data-testid={`impact-row-${i}-client-positioning`}>
                                                        <div className="text-[hsl(var(--mute))] uppercase tracking-wider text-[10px] font-medium">{clientDisplayName} foothold</div>
                                                        <p className="text-xs text-[hsl(var(--ink-soft))] leading-relaxed mt-1">{positioningBody}</p>
                                                    </div>
                                                )}

                                                {/* 3. Recommended Response — cell-level recommendation only */}
                                                {recommended && (
                                                    <div className="mt-2" data-testid={`impact-row-${i}-recommended`}>
                                                        <div className="text-[hsl(var(--mute))] uppercase tracking-wider text-[10px] font-medium">Recommended response</div>
                                                        <p className="text-xs text-[hsl(var(--ink-soft))] leading-relaxed mt-1">{recommended}</p>
                                                    </div>
                                                )}

                                                {/* Methodology — evidence basis, confidence badge, and confidence
                                                    reason are intentionally NOT rendered in the strategy/innovation
                                                    audience view. The underlying fields (`evidenceBasis`,
                                                    `confidence`, `confidenceReason`) remain available on the
                                                    `impactCellAssessments` payload for internal/audit consumers
                                                    and can be re-enabled here if a Methodology view is added later. */}
                                            </li>
                                        );
                                    })}
                            </ul>
                        </div>
                    )}
                </>
            )}
        </section>
    );
};

export default ScopedImpactMatrix;
